import React, { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useRole } from '../hooks/useRole';
import { useSidebarOpenForDashboardLayout } from '../hooks/useSidebarOpenForDashboardLayout';
import { useAuth } from '../contexts/AuthContext';
import { navigateToLogin } from '../utils/navigationUtils';
import { generateInitials } from '../utils/userUtils';
import { logger } from '../utils/logger';
import { NotificationBell } from '../components/NotificationBell';
import { ChatSessionProvider } from '../contexts/ChatSessionContext';
import { changePassword } from '../services/api';
import dashboardIcon from '../assets/icons/dashboard-menu-icon.svg';
import chatIcon from '../assets/icons/chat-menu-icon.svg';
import chatBotIcon from '../assets/icons/bxs_bot.svg';
import usersIcon from '../assets/icons/users-menu-icon.svg';
import signOut from '../assets/icons/signOut.png';

// Helper function to format timestamp
const formatLastLogin = (timestamp: string | null | undefined): string => {
    if (!timestamp) return '-';

    try {
        const date = new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    } catch {
        return '-';
    }
};

export const ManagerLayout: React.FC = () => {
    const { hasRole } = useRole();
    const location = useLocation();
    const { isSidebarOpen, setSidebarOpen, isDesktop } = useSidebarOpenForDashboardLayout();

    const titleMap: Record<string, string> = {
        '/manager': 'Dashboard',
        '/manager/chatbot': 'Chatbot',
        '/manager/past-chats': 'Past Chats',
        '/manager/my-workers': 'My Workers',
    };

    // Update browser tab title
    useEffect(() => {
        const pageTitle = titleMap[location.pathname] || 'Dashboard';
        document.title = `${pageTitle} - Renegade`;
    }, [location.pathname]);

    useEffect(() => {
        if (!isDesktop) {
            setSidebarOpen(false);
        }
    }, [location.pathname, isDesktop, setSidebarOpen]);

    if (!hasRole('manager')) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h1 className="text-2xl font-medium text-red-600 mb-4">Access Denied</h1>
                    <p className="text-gray-600">You don't have permission to access the manager area.</p>
                </div>
            </div>
        );
    }

    const currentTitle = titleMap[location.pathname] || 'Dashboard';
    // Chatbot fills the viewport with its own scroll areas; other manager pages scroll in the outlet.
    const isManagerChatbotRoute = location.pathname === '/manager/chatbot';
    const dashboardOutletClassName = [
        'dashboard-outlet',
        !isManagerChatbotRoute ? 'dashboard-outlet--scroll-main' : '',
    ].filter(Boolean).join(' ');

    return (
        <div className="flex h-full min-h-0 max-h-[100dvh] overflow-hidden bg-gray-50 font-[Arial]">
            {/* Mobile Overlay */}
            {isSidebarOpen && !isDesktop && (
                <div className="mobile-overlay" onClick={() => setSidebarOpen(false)} aria-hidden />
            )}

            {/* Sidebar - Card Style */}
            <div className={`db-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}>
                <div>
                    <div className="sidebar-logo">
                        <img src="/logo.png" alt="Renegade Logo" />
                    </div>
                    <nav className="sidebar-nav">
                        <NavLink
                            to="/manager"
                            end
                            className={({ isActive }) => `side-nav-item ${isActive ? "side-nav-item-active" : ""}`}
                        >
                            <img src={dashboardIcon} alt="Dashboard" />
                            Dashboard
                        </NavLink>
                        <NavLink
                            to="/manager/chatbot"
                            className={({ isActive }) => `side-nav-item ${isActive ? "side-nav-item-active" : ""}`}
                        >
                            <img src={chatBotIcon} alt="Chatbot" />
                            Chatbot
                        </NavLink>
                        <NavLink
                            to="/manager/past-chats"
                            className={({ isActive }) => `side-nav-item ${isActive ? "side-nav-item-active" : ""}`}
                        >
                            <img src={chatIcon} alt="Past Chats" />
                            Past Chats
                        </NavLink>
                        <NavLink
                            to="/manager/my-workers"
                            className={({ isActive }) => `side-nav-item ${isActive ? "side-nav-item-active" : ""}`}
                        >
                            <img src={usersIcon} alt="My Workers" />
                            My Workers
                        </NavLink>
                    </nav>
                </div>
            </div>

            {/* Main Content */}
            <main className="dashboard-body">
                {/* Mobile Header */}
                <div className="mobile-header dashboard-header-drawer-mode">
                    <div className="d-flex align-items-center justify-content-between w-100">
                        <div className="d-flex align-items-center">
                            <button
                                type="button"
                                className="hamburger-btn me-3"
                                onClick={() => setSidebarOpen((open) => !open)}
                            >
                                <i className="iconoir-menu-scale"></i>
                            </button>
                            <h3 className="grey-800 mb-0">{currentTitle}</h3>
                        </div>
                        <div className="d-flex align-items-center gap-3">
                            <NotificationBell />
                            <HeaderProfile isMobile={true} />
                        </div>
                    </div>
                </div>

                {/* Desktop Header */}
                <div className="desktop-header dashboard-header-desktop-only dashboard-header">
                    <div className="d-flex justify-content-between align-items-center">
                        <h3 className="grey-800 mb-0">{currentTitle}</h3>
                        <div className="d-flex align-items-center gap-3">
                            <NotificationBell />
                            <HeaderProfile isMobile={false} />
                        </div>
                    </div>
                </div>

                <ChatSessionProvider>
                    <div className={dashboardOutletClassName}>
                        <Outlet />
                    </div>
                </ChatSessionProvider>
            </main>
        </div>
    );
};

// Manager Profile Modal Component
function ManagerProfileModal({ show, onClose, onOpenChangePassword }: {
    show: boolean;
    onClose: () => void;
    onOpenChangePassword: () => void;
}) {
    const { user } = useAuth();

    return (
        <Modal show={show} onHide={onClose} className='std-modal-width' centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-gray-800">Manager Profile</Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={onClose}></i>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                {/* Profile Fields */}
                <div>
                    <div className="mb-3">
                        <label className="text-14-bold grey-800 mb-0">
                            Full Name
                        </label>
                        <p className="text-14-reg grey-600">
                            {user?.full_name || '-'}
                        </p>
                    </div>
                    <div className="mb-3">
                        <label className="text-14-bold grey-800 mb-0">
                            Email Address
                        </label>
                        <p className="text-14-reg grey-600">
                            {user?.email ?? '-'}
                        </p>
                    </div>
                    <div className="mb-3">
                        <label className="text-14-bold grey-800 mb-0">
                            Role
                        </label>
                        <p className="text-14-reg grey-600 mt-1">
                            <span className='px-3 py-1 rounded bg-light'>Manager</span>
                        </p>
                    </div>
                    <div className="mb-3">
                        <button
                            className='renegade-btn-primary mini-btn px-4 py-1'
                            style={{ fontSize: '14px', borderRadius: '8px' }}
                            onClick={onOpenChangePassword}
                        >
                            Change Password
                        </button>
                    </div>
                    <div>
                        <label className="text-14-bold grey-800 mb-0">
                            Last Login Timestamp
                        </label>
                        <p className="text-14-reg grey-600">
                            {formatLastLogin(user?.last_login_at)}
                        </p>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

// Logout Confirmation Modal Component
function LogoutConfirmationModal({
    show,
    onClose,
    onConfirm
}: {
    show: boolean;
    onClose: () => void;
    onConfirm: () => void;
}) {
    return (
        <Modal show={show} onHide={onClose} className='mini-modal-width' centered backdrop="static">
            <Modal.Header>
                <Modal.Title className="text-gray-800">Sign Out</Modal.Title>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <div className="text-left">
                    <p className="text-gray-600 mb-6">Are you sure you want to sign out?</p>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end gap-3">
                        <button
                            onClick={onClose}
                            className="renegade-btn-secondary mini-btn"
                        >
                            No
                        </button>
                        <button
                            onClick={onConfirm}
                            className="renegade-btn-primary mini-btn"
                        >
                            Yes
                        </button>
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
}

// Change Password Modal Component
function ChangePasswordModal({ show, onClose }: {
    show: boolean;
    onClose: () => void;
}) {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [passwordError, setPasswordError] = useState<string>("");
    const [passwordSuccess, setPasswordSuccess] = useState<string>("");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});

    const { user } = useAuth();

    const validatePasswordForm = () => {
        const newErrors: { [key: string]: string } = {};

        if (!currentPassword) {
            newErrors.currentPassword = 'Current password is required.';
        }

        if (!newPassword) {
            newErrors.newPassword = 'New password is required.';
        } else if (newPassword.length < 8) {
            newErrors.newPassword = 'Password must be at least 8 characters long.';
        } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=])[A-Za-z\d@$!%*?&#\-_+=]{8,}$/.test(newPassword)) {
            newErrors.newPassword = 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
        }

        if (!confirmPassword) {
            newErrors.confirmPassword = 'Confirm password is required.';
        } else if (newPassword !== confirmPassword) {
            newErrors.confirmPassword = 'Confirm password must match the new password.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePasswordField = (fieldName: string, value: string) => {
        const newErrors = { ...errors };

        if (fieldName === 'currentPassword') {
            if (!value) {
                newErrors.currentPassword = 'Current password is required.';
            } else {
                delete newErrors.currentPassword;
            }
        } else if (fieldName === 'newPassword') {
            if (!value) {
                newErrors.newPassword = 'New password is required.';
            } else if (value.length < 8) {
                newErrors.newPassword = 'Password must be at least 8 characters long.';
            } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#\-_+=])[A-Za-z\d@$!%*?&#\-_+=]{8,}$/.test(value)) {
                newErrors.newPassword = 'Password must include at least one uppercase letter, one lowercase letter, one number, and one special character.';
            } else {
                delete newErrors.newPassword;
            }

            // Also validate confirm password when new password changes
            if (confirmPassword && confirmPassword !== value) {
                newErrors.confirmPassword = 'Confirm password must match the new password.';
            } else if (confirmPassword && confirmPassword === value) {
                delete newErrors.confirmPassword;
            }
        } else if (fieldName === 'confirmPassword') {
            if (!value) {
                newErrors.confirmPassword = 'Confirm password is required.';
            } else if (newPassword !== value) {
                newErrors.confirmPassword = 'Confirm password must match the new password.';
            } else {
                delete newErrors.confirmPassword;
            }
        }

        setErrors(newErrors);
    };

    const toggleCurrentPasswordVisibility = () => {
        setShowCurrentPassword(!showCurrentPassword);
    };

    const toggleNewPasswordVisibility = () => {
        setShowNewPassword(!showNewPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    const handlePasswordSubmit = async () => {
        setIsSubmitting(true);
        setPasswordError("");
        setPasswordSuccess("");
        setErrors({});

        // Validation using the new validation function
        if (!validatePasswordForm()) {
            setIsSubmitting(false);
            return;
        }

        try {
            // Make API call to update password
            await changePassword(currentPassword, newPassword, confirmPassword);

            // Password updated successfully
            toast.success("Password updated successfully!");
            setCurrentPassword("");
            setNewPassword("");
            setConfirmPassword("");
            setPasswordError("");

            // Auto-close the modal after 1 second
            setTimeout(() => {
                onClose();
            }, 1000);
        } catch (error: unknown) {
            logger.error("Error updating password:", error);
            toast.error((error as Error)?.message || "An error occurred while updating the password. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onClose} centered>
            <Modal.Header>
                <Modal.Title className="text-gray-800">Change Password</Modal.Title>
                <i className="iconoir-xmark text-20-reg cursor-pointer" onClick={onClose}></i>
            </Modal.Header>

            <Modal.Body className="px-4 py-4">
                <div className="form-group password-feild">
                    <label htmlFor="currentPassword">
                        Current Password
                    </label>
                    <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={currentPassword}
                        onChange={(e) => {
                            setCurrentPassword(e.target.value);
                            validatePasswordField('currentPassword', e.target.value);
                        }}
                        placeholder="Enter current password"
                        className={`form-control ${errors.currentPassword ? 'border-danger' : ''}`}
                    />
                    <button type="button" onClick={toggleCurrentPasswordVisibility} className='password-toggle'>
                        {showCurrentPassword ? (
                            <i className="iconoir-eye"></i>
                        ) : (
                            <i className="iconoir-eye-closed"></i>
                        )}
                    </button>
                    {errors.currentPassword && (
                        <p className="error-message">{errors.currentPassword}</p>
                    )}
                </div>
                <div className="form-group password-feild">
                    <label htmlFor="newPassword">
                        New Password
                    </label>
                    <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={newPassword}
                        onChange={(e) => {
                            setNewPassword(e.target.value);
                            validatePasswordField('newPassword', e.target.value);
                        }}
                        placeholder="Enter new password"
                        className={`form-control ${errors.newPassword ? 'border-danger' : ''}`}
                    />
                    <button type="button" onClick={toggleNewPasswordVisibility} className='password-toggle'>
                        {showNewPassword ? (
                            <i className="iconoir-eye"></i>
                        ) : (
                            <i className="iconoir-eye-closed"></i>
                        )}
                    </button>
                    {errors.newPassword && (
                        <p className="error-message">{errors.newPassword}</p>
                    )}
                </div>
                <div className="form-group password-feild">
                    <label htmlFor="confirmPassword">
                        Confirm New Password
                    </label>
                    <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => {
                            setConfirmPassword(e.target.value);
                            validatePasswordField('confirmPassword', e.target.value);
                        }}
                        placeholder="Confirm new password"
                        className={`form-control ${errors.confirmPassword ? 'border-danger' : ''}`}
                    />
                    <button type="button" onClick={toggleConfirmPasswordVisibility} className='password-toggle'>
                        {showConfirmPassword ? (
                            <i className="iconoir-eye"></i>
                        ) : (
                            <i className="iconoir-eye-closed"></i>
                        )}
                    </button>
                    {errors.confirmPassword && (
                        <p className="error-message">{errors.confirmPassword}</p>
                    )}
                </div>
                <div className='d-flex justify-content-end mt-5 gap-15'>
                    <button className='renegade-btn-secondary mini-btn' onClick={onClose}>
                        Cancel
                    </button>
                    <button className='renegade-btn-primary mini-btn' onClick={handlePasswordSubmit} disabled={isSubmitting}>
                        {isSubmitting ? 'Updating...' : 'Update Password'}
                    </button>
                </div>
            </Modal.Body>
        </Modal>
    );
}

function HeaderProfile({ isMobile }: { isMobile: boolean }) {
    const [open, setOpen] = useState(false);
    const [showProfileModal, setShowProfileModal] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const { logout, user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        function onClickOutside(event: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setOpen(false);
            }
        }
        window.addEventListener('mousedown', onClickOutside);
        return () => window.removeEventListener('mousedown', onClickOutside);
    }, []);

    return (
        <div ref={containerRef} className="position-relative">
            <button type="button" onClick={() => setOpen((v) => !v)} className="d-flex align-items-center gap-3 border-0 bg-transparent">
                <div className="user-profile-circle">
                    {user?.full_name ? generateInitials(user.full_name) : 'M'}
                </div>
                {!isMobile && <h4 className="mb-0 grey-700">{user?.full_name || 'Manager'}</h4>}
            </button>
            {open && (
                <div className="header-profile-dropdown">
                    <div className="px-4 py-3">
                        <p className="grey-800 text-16-bold mb-0">{user?.full_name || 'Manager'}</p>
                        <p className="grey-600 text-16-reg mb-0">{user?.email ?? 'manager@renegade.com'}</p>
                    </div>
                    <div className="border-top" />
                    <button onClick={() => { setShowProfileModal(true); setOpen(false); }} className="w-100 d-flex align-items-center gap-3 px-4 py-3 border-0 bg-transparent text-start" style={{ transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <span className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px' }}>
                            <i className="iconoir-user text-20-reg"></i>
                        </span>
                        <span className="text-16-bold grey-900">Profile</span>
                    </button>
                    <div className="border-top" />
                    <button onClick={() => { setShowLogoutModal(true); setOpen(false); }} className="w-100 d-flex align-items-center gap-3 px-4 py-3 border-0 bg-transparent text-start" style={{ transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8d7da'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                        <span className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: '36px', height: '36px', backgroundColor: '#f8d7da' }}>
                            <img src={signOut} alt="Sign out" style={{ width: '16px', height: '16px' }} />
                        </span>
                        <span className="text-16-bold color-renegade">Sign out</span>
                    </button>
                </div>
            )}

            {/* Profile Modal */}
            <ManagerProfileModal
                show={showProfileModal}
                onClose={() => setShowProfileModal(false)}
                onOpenChangePassword={() => {
                    setShowProfileModal(false);
                    setShowChangePasswordModal(true);
                }}
            />

            {/* Change Password Modal */}
            <ChangePasswordModal
                show={showChangePasswordModal}
                onClose={() => {
                    setShowChangePasswordModal(false);
                    setShowProfileModal(true);
                }}
            />

            {/* Logout Confirmation Modal */}
            <LogoutConfirmationModal
                show={showLogoutModal}
                onClose={() => setShowLogoutModal(false)}
                onConfirm={() => {
                    logout();
                    setShowLogoutModal(false);
                    toast.success("Signed out successfully!");
                    navigateToLogin(navigate);
                }}
            />
        </div>
    );
}
